/**
 * **控制帧**（control frame）的词表——不是业务帧的那两个。
 *
 * 控制帧**不进帧目录**（后端 `backend/frames/catalog.yaml` 只装业务帧），
 * 由**流端点自己写**给客户端看：没有收件人、不落行、不进发布层。
 *
 * ## ⚠️ 为什么要有控制帧（这一段是**唯一**的出处）
 *
 * `EventSource` **看不到 HTTP 状态码** ⇒ 建流拿到 401 时客户端只看到
 * 「连接出错」⇒ 客户端**分不出**「这个会话已经没了」与「网络抖了一下」。
 * 而这两件事要做的处置**正好相反**：前者要清场回登录页，后者要重连。
 *
 * ⇒ 只能靠服务端**主动说一声**：写一帧 `stream.reconnect`（带 `reason`）再收流。
 * 后端在撤销 / 禁用 / 软删之后都走这一条（ADR 0007 D2）。
 *
 * ⚠️ **取值必须与后端逐字一致**：
 * 后端 `backend/src/frames/router.py::REASON_SESSION_REVOKED` /
 * `::REASON_SESSION_DISABLED` ⇒ 两边各有一份，改一处要改另一处。
 * （后端那两个常量是「发什么」，这里是「认什么」。）
 */

/** 控制帧 `stream.reconnect` 的 `reason`——会话没了（令牌被撤销）。 */
export const REASON_SESSION_REVOKED = 'session_revoked';

/** 控制帧 `stream.reconnect` 的 `reason`——会话没了（用户被禁用或软删）。 */
export const REASON_SESSION_DISABLED = 'session_disabled';

/**
 * 「这个 reason 意味着会话已经结束了」的取值集合。
 *
 * ⚠️ **不是** `reason !== 'too_slow'` 那种反向判据：反向判据把**将来新增的**
 * reason 全算成「会话没了」⇒ 一个新 reason 就能把客户端踢下线。
 * 正向白名单 ⇒ 新 reason 默认**不**踢人，要踢得显式加进来。
 */
export const SESSION_ENDED_REASONS: ReadonlySet<string> = new Set([
  REASON_SESSION_REVOKED,
  REASON_SESSION_DISABLED,
]);

/**
 * 这个信封是不是「会话结束了」的信号。
 *
 * 只有 `stream.reconnect` + 白名单里的 reason 才算 ⇒ 其余控制帧
 * （如 `too_slow`：服务端主动摘掉慢消费者）**只是重连**，不踢会话。
 */
export function isSessionEnded(envelope: { type?: unknown; reason?: unknown }): boolean {
  if (envelope.type !== 'stream.reconnect') return false;
  if (typeof envelope.reason !== 'string') return false;
  return SESSION_ENDED_REASONS.has(envelope.reason);
}
